import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getRecommendations } from '../../../../api/client';
import type {
  ModuleRecommendation,
  RecommendationResponse,
  SourceReference,
} from '../../../../types';
import {
  STRENGTH_SCORE_THRESHOLD,
  type GrowthPlanSource,
  type GrowthPlanState,
  type GrowthPlanStatus,
  type GrowthPlanSuggestion,
} from '../types';

function resolveLocale(record: Record<string, string> | undefined, locale: string): string {
  if (!record) return '';
  return record[locale] ?? record.en ?? Object.values(record)[0] ?? '';
}

function deriveStatus(score: number | null): GrowthPlanStatus {
  if (score === null) return 'developing';
  return score >= STRENGTH_SCORE_THRESHOLD ? 'strength' : 'developing';
}

function mapSources(sources: SourceReference[] | null | undefined): GrowthPlanSource[] {
  if (!sources || sources.length === 0) return [];
  return sources.map((s) => ({ url: s.url, title: s.title ?? null }));
}

function mapSuggestion(
  module: ModuleRecommendation,
  locale: string,
): GrowthPlanSuggestion {
  const score = module.score ?? null;
  return {
    moduleId: module.module_id,
    moduleName: resolveLocale(module.module_name, locale),
    score,
    status: deriveStatus(score),
    actionTitle: module.action_title ?? null,
    description: module.description ?? null,
    reflect: module.reflect ?? null,
    steps: module.steps ?? null,
    duration: module.duration ?? null,
    level: module.level ?? null,
    expectedOutcome: module.expected_outcome ?? null,
    sources: mapSources(module.sources),
    isRead: Boolean(module.is_read),
  };
}

/**
 * Fetches the active SelfCare recommendation for the current user and exposes
 * it in a discriminated-union state for the Growth Plan UI.
 *
 * Notes:
 * - The BFF endpoint returns an array wrapping a single recommendation (or [])
 * - No regenerate function — generation is server-side only
 * - Older recommendations may lack the new Growth Plan fields; those map to
 *   `null`/empty arrays but the suggestion is still emitted so the user sees
 *   the legacy module score & name.
 */
export function useGrowthPlan(locale: string): GrowthPlanState {
  const [data, setData] = useState<RecommendationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  // Track in-flight request id to ignore stale responses on rapid refetch.
  const requestIdRef = useRef(0);

  const fetchPlan = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setErrored(false);
    try {
      const recs = await getRecommendations(locale);
      if (requestId !== requestIdRef.current) return;
      setData(recs.length > 0 ? recs[0] : null);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('[useGrowthPlan] fetch failed', err);
      setErrored(true);
      setData(null);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [locale]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  return useMemo<GrowthPlanState>(() => {
    if (loading) return { state: 'loading' };
    if (errored) return { state: 'error', retry: () => void fetchPlan() };
    if (!data) return { state: 'empty' };

    const modules = data.module_recommendations ?? [];
    if (modules.length === 0) return { state: 'empty' };

    const suggestions = modules.map((m) => mapSuggestion(m, locale));
    return {
      state: 'ready',
      suggestions,
      recommendationId: data.id,
      createdAt: data.created_at,
    };
  }, [loading, errored, data, locale, fetchPlan]);
}
